import _ from 'lodash';

export const ENTER = 13 as const;
export const SPACE = 32 as const;
export const TAB = 9 as const;
export const ESC = 27 as const;

export const SHIFT = 16 as const;
export const CTRL = 17 as const;
export const ALT = 18 as const;

export const LEFT = 37 as const;
export const UP = 38 as const;
export const RIGHT = 39 as const;
export const DOWN = 40 as const;

export const END = 35 as const;
export const HOME = 36 as const;

export const BACKSPACE = 8 as const;
export const DELETE = 46 as const;

export const A = 65 as const;
export const B = 66 as const;
export const C = 67 as const;
export const E = 69 as const;
export const H = 72 as const;
export const I = 73 as const;
export const K = 75 as const;
export const S = 83 as const;
export const U = 85 as const;
export const V = 86 as const;
export const X = 88 as const;
export const Z = 90 as const;

export const DASH = 189 as const;
export const QUOTE = 222 as const;
export const COMMA = 188 as const;
export const ASTERISK = 56 as const;
export const UNDERSCORE = 189 as const;
export const OPENING_PARENTHESES = 57 as const;
export const CLOSING_PARENTHESES = 48 as const;
export const BACKTICK = 192 as const;

export const NBSP = 160 as const;

/**
 * Given a string, e.g. 'shift+f' returns a function that tells you wether any given keyboard event matches.
 * @param  {String}  combo  A string representation of a keyboard event.
 * @return {Function}     Takes a keyboard event and returns true if it matches the string.
 */
export function is(combo: string) {
  const keyCombinations = combo.split('/').map(function (keyCombo) {
    const tokenized = tokenize(keyCombo);
    const matchesEvent = _.matches(
      tokenized.reduce(
        function (memo, token) {
          return _.extend(memo, token);
        },
        {
          metaKey: false,
          altKey: false,
          ctrlKey: false,
          shiftKey: false,
        }
      )
    );
    return matchesEvent;
  });
  return function (e: React.KeyboardEvent<HTMLInputElement>) {
    return keyCombinations.some(function (isCombo) {
      return isCombo(e);
    });
  };
}

/**
 * Determines if the key is an arrow key.
 * @param {Number} key
 * @return {Boolean}
 */
export function isArrow(key: number) {
  return key === LEFT || key === RIGHT || key === UP || key === DOWN;
}

/**
 * Determines if the key is a standard modifier.
 * @param {Number} key
 * @return {Boolean}
 */
export function isModifier(key: number) {
  return key === SHIFT || key === CTRL || key === ALT;
}

/**
 * Determines if the key is a non-character key.
 * @param {Number} key
 * @return {Boolean}
 */
export function isNonChar(key: number) {
  return (
    isArrow(key) ||
    key === SPACE ||
    key === ENTER ||
    key === BACKSPACE ||
    key === DELETE ||
    key === TAB ||
    key === ESC
  );
}

// On latest chrome it seems e.metaKey is always false
export function isMetaKey(e: React.KeyboardEvent<HTMLInputElement>) {
  const key = e.key;
  return e.metaKey || key === 'Meta';
}

/**
 * Determines whether an event was intended for a new tab.
 * This should happen for a click event when:
 *  - Middle mouse button is clicked
 *  - Command or Ctrl are held down while the mouse button is clicked
 */
export function hasNewTabModifier(event: React.MouseEvent<HTMLElement>) {
  // button: 0 left, 1 middle, 2 right — middle-click opens a new tab.
  return event.button === 1 || event.metaKey || event.ctrlKey;
}

const modifierKeys = {
  shift: {shiftKey: true},
  alt: {altKey: true},
  ctrl: {ctrlKey: true},
  meta: {metaKey: true},
  command: {metaKey: true},
} as const;

type modifierKeysType = keyof typeof modifierKeys;

const specialKeys = {
  enter: {which: ENTER},
  esc: {which: ESC},
  tab: {which: TAB},
  up: {which: UP},
  right: {which: RIGHT},
  down: {which: DOWN},
  left: {which: LEFT},
} as const;

type specialKeysType = keyof typeof specialKeys;

function modifierKey(s: string) {
  return modifierKeys[s as modifierKeysType] || null;
}

function regularKey(s: string) {
  return {which: s.toUpperCase().charCodeAt(0)};
}

function specialKey(s: string) {
  return specialKeys[s as specialKeysType] || null;
}

function tokenize(string: string) {
  const parts = string.toLowerCase().split(/\b/g);
  return parts.map(function (part) {
    const token = modifierKey(part);
    if (token) {
      return token;
    } else {
      if (part.length === 1) {
        return regularKey(part);
      } else {
        return specialKey(part);
      }
    }
  });
}
